import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
} from '@react-pdf/renderer';
import {
  CustomerOutstandingReportResponse,
  CustomerOutstandingReportItem,
} from 'src/models/Management.interface';
import { getCompanyInfoForPDF, CompanyInfoData } from 'src/utils/company.utils';
import { formatCurrency } from 'src/utils/pdf.utils';
import Logo from 'src/assets/images/Logo.png';
import dayjs from 'dayjs';

const colors = {
  primary: '#1a3c5e',
  textDark: '#1a1a2e',
  textMedium: '#555555',
  textLight: '#888888',
  border: '#e0e0e0',
  borderLight: '#f0f0f0',
  backgroundGray: '#f7f8fa',
  white: '#ffffff',
  errorRed: '#c0392b',
};

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    backgroundColor: colors.white,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    paddingBottom: 20,
  },
  companyInfo: {
    flexDirection: 'column',
  },
  companyName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 4,
  },
  companyDetails: {
    fontSize: 9,
    color: colors.textMedium,
    marginBottom: 2,
  },
  reportTitleContainer: {
    alignItems: 'flex-end',
  },
  reportTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 4,
  },
  reportDate: {
    fontSize: 10,
    color: colors.textMedium,
  },
  filterSection: {
    marginBottom: 20,
    padding: 12,
    backgroundColor: colors.backgroundGray,
    borderRadius: 8,
  },
  filterTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  filterGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  filterItem: {
    width: '50%',
    marginBottom: 4,
  },
  filterLabel: {
    fontSize: 8,
    color: colors.textLight,
  },
  filterValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.textDark,
  },
  summarySection: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 15,
    marginBottom: 24,
  },
  statCard: {
    width: '32%',
    padding: 10,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.borderLight,
    borderRadius: 8,
    alignItems: 'flex-end',
  },
  statLabel: {
    fontSize: 7,
    color: colors.textLight,
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  statValue: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.textDark,
  },
  statValueError: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.errorRed,
  },
  table: {
    width: '100%',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.primary,
    padding: 8,
  },
  tableHeaderCell: {
    color: colors.white,
    fontSize: 8,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
    padding: 8,
  },
  tableCell: {
    fontSize: 8,
    color: colors.textDark,
  },
  tableCellError: {
    fontSize: 8,
    color: colors.errorRed,
    fontWeight: 'bold',
  },
  cellName: { width: '40%' },
  cellAmount: { width: '20%', textAlign: 'right' },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: 10,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 8,
    color: colors.textLight,
  },
  poweredBy: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    width: 40,
    height: 12,
    marginLeft: 4,
  },
});

interface CustomerOutstandingReportPDFProps {
  data: CustomerOutstandingReportResponse;
  filters: {
    customerName: string | null;
  };
  companyInfo?: CompanyInfoData;
}

const CustomerOutstandingReportDocument: React.FC<
  CustomerOutstandingReportPDFProps
> = ({ data, filters, companyInfo }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>
              {companyInfo?.companyName || 'OrderX Management'}
            </Text>
            <Text style={styles.companyDetails}>
              {companyInfo?.addressLine1 || ''}
            </Text>
            <Text style={styles.companyDetails}>
              {companyInfo?.city ? `${companyInfo.city}, ` : ''}
              {companyInfo?.postCode || ''}
            </Text>
            <Text style={styles.companyDetails}>
              {companyInfo?.email || ''}
            </Text>
          </View>
          <View style={styles.reportTitleContainer}>
            <Text style={styles.reportTitle}>Customer Outstanding Report</Text>
            <Text style={styles.reportDate}>
              Generated: {dayjs().format('DD MMM YYYY, HH:mm')}
            </Text>
          </View>
        </View>

        <View style={styles.filterSection}>
          <Text style={styles.filterTitle}>Report Parameters</Text>
          <View style={styles.filterGrid}>
            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>Customer</Text>
              <Text style={styles.filterValue}>
                {filters.customerName || 'All Customers'}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.summarySection}>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Grand Total</Text>
            <Text style={styles.statValue}>
              {formatCurrency(data.summary.totalGrandTotal)}
            </Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Paid Amount</Text>
            <Text style={styles.statValue}>
              {formatCurrency(data.summary.totalPaidAmount)}
            </Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Total Outstanding</Text>
            <Text style={styles.statValueError}>
              {formatCurrency(data.summary.totalOutstanding)}
            </Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.cellName]}>
              Customer
            </Text>
            <Text style={[styles.tableHeaderCell, styles.cellAmount]}>
              Grand Total
            </Text>
            <Text style={[styles.tableHeaderCell, styles.cellAmount]}>
              Paid Amount
            </Text>
            <Text style={[styles.tableHeaderCell, styles.cellAmount]}>
              Outstanding
            </Text>
          </View>

          {data.customers.map(
            (customer: CustomerOutstandingReportItem, index: number) => (
              <View
                key={customer.customerId}
                style={[
                  styles.tableRow,
                  { backgroundColor: index % 2 === 0 ? '#fff' : '#fcfcfc' },
                ]}
              >
                <Text style={[styles.tableCell, styles.cellName]}>
                  {customer.customerName}
                </Text>
                <Text style={[styles.tableCell, styles.cellAmount]}>
                  {formatCurrency(customer.grandTotal)}
                </Text>
                <Text style={[styles.tableCell, styles.cellAmount]}>
                  {formatCurrency(customer.paidAmount)}
                </Text>
                <Text style={[styles.tableCellError, styles.cellAmount]}>
                  {formatCurrency(customer.balanceAmount)}
                </Text>
              </View>
            )
          )}
        </View>

        <View style={styles.footer} fixed>
          <View style={styles.poweredBy}>
            <Text style={styles.footerText}>POWERED BY</Text>
            <Image style={styles.logo} src={Logo} />
          </View>
        </View>
      </Page>
    </Document>
  );
};

export const generateCustomerOutstandingReportPDF = async (
  data: CustomerOutstandingReportResponse,
  filters: {
    customerName: string | null;
  }
): Promise<void> => {
  try {
    const companyInfo = await getCompanyInfoForPDF();

    const blob = await pdf(
      <CustomerOutstandingReportDocument
        data={data}
        filters={filters}
        companyInfo={companyInfo}
      />
    ).toBlob();

    const fileName = `CustomerOutstandingReport-${dayjs().format(
      'YYYYMMDD-HHmm'
    )}.pdf`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error) {
    console.error('Error generating Customer Outstanding Report PDF:', error);
    throw error;
  }
};
